import { StatusChipVariant } from "@indigo-components";
import {
  AppointmentStatus,
  AppointmentType,
  BillingStatus,
  NoShowReason,
  ParticipantType,
  ReportSource,
  ReportStatus,
} from "~/api/API";
import { extractDateWithDbDefault } from "~/common/converters";
import { getTodayDateISOStr } from "~/common/utils";
import { AuthUserGroup } from "~/types/auth";

export enum DoctorStatusEnum {
  ReportApproved = "Report approved",
  // Supplemental reports don't require a written assessment from the physician,
  // so pre-publish they're being processed by the team — not awaiting doctor
  // action. Shown instead of "Assessment required" for supplementals.
  ProcessingSupplemental = "Processing supplemental",
}

export enum MAStatusEnum {
  CorrectingPayments = "Correcting payments",
  PaidPhysician = "Paid physician",
  ReadyForFinalReview = "Ready for final review",
  ReadyToSubmit = "Ready to submit",
  Billed = "Billed",
  ReschedulingHold = "Rescheduling hold",
  LateReschedulingHold = "Rescheduling hold",
  DepositionHold = "Deposition hold",
  ReevalHold = "Re-eval hold",
}

export enum CommonStatusEnum {
  Scheduled = "Scheduled",
  Inquiry = "Inquiry",
  InquiryClosed = "Inquiry closed",
  Cancelled = "Cancelled",
  LateCancelled = "Late cancelled",
  Rescheduled = "Rescheduled",
  LateRescheduled = "Late rescheduled",
  RescheduledPatient = "Rescheduled (Patient)",
  LateRescheduledPatient = "Late rescheduled (Patient)",
  RescheduledADJ = "Rescheduled (ADJ)",
  LateRescheduledADJ = "Late rescheduled (ADJ)",
  RescheduledAADA = "Rescheduled (AA/DA)",
  LateRescheduledAADA = "Late rescheduled (AA/DA)",
  RescheduledInternal = "Rescheduled (Internal)",
  LateRescheduledInternal = "Late rescheduled (Internal)",
  NoShowPatient = "No show (Patient)",
  NoShowInterpreter = "No show (Interpreter)",
  NoShow = "No show",
  AssessmentRequired = "Assessment required",
  AppointmentCompleted = "Appointment completed",
  PaymentReceived = "Payment received",
  ReportReadyForReview = "Report ready for review",
  ReportSubmitted = "Report submitted",
  ReschedulingInProgress = "Rescheduling in progress",
}

export type AppointmentStatusEnum =
  | CommonStatusEnum
  | DoctorStatusEnum
  | MAStatusEnum;

export type UserRole = AuthUserGroup | null;

/** Minimal appointment fields needed by getDisplayState */
export interface AppointmentStatusInfo {
  status?: AppointmentStatus | null;
  type?: AppointmentType | null;
  date?: string | null;
  billingStatus?: BillingStatus | null;
  reportStatus?: ReportStatus | null;
  reschedulingRequestedBy?: ParticipantType | null;
  reschedulingRequestedInternally?: boolean | null;
  noShowReason?: NoShowReason | null;
  assessment?: { markedAsDone?: boolean | null } | null;
  reportConfiguration?: { assessment?: ReportSource | null } | null;
}

// TODO (Jason): review the function and adjust for edge cases
export const getDisplayState = (
  role: UserRole,
  appointment: AppointmentStatusInfo,
  verbose = true,
) => {
  switch (role) {
    case AuthUserGroup.DOCTOR:
      return getDisplayStateForDoctor(appointment, false);
    case AuthUserGroup.MEDICAL_ASSISTANT:
    case AuthUserGroup.HISTORIAN:
    case AuthUserGroup.ADMIN:
      return getDisplayStateForMedicalAssistant(appointment, verbose);
    default:
      return;
  }
};

export const getDisplayStateForDoctor = (
  appointment: AppointmentStatusInfo,
  verbose = true,
): DoctorStatusEnum | CommonStatusEnum | undefined => {
  // Check billing states first
  switch (appointment.billingStatus) {
    case BillingStatus.PHYSICIAN_PAID:
      // For doctor, we simply show the payment received status when the physician is paid
      return CommonStatusEnum.PaymentReceived;
    case BillingStatus.BILLED:
    case BillingStatus.PAYMENT_RECEIVED:
    case BillingStatus.CORRECTING_PAYMENTS:
    case BillingStatus.PAYMENT_DISPUTED:
      // If in billing cycle, assume the report is submitted
      return CommonStatusEnum.ReportSubmitted;
  }

  // Occurrence states other than SCHEDULED take precedence over reportStatus.
  // reportStatus only advances while an appointment is on the normal SCHEDULED
  // path, so a report published before the appointment was cancelled / no-showed
  // / rescheduled must not mask the real lifecycle state. GET-3429.
  switch (appointment.status) {
    case AppointmentStatus.CANCELLED:
      return CommonStatusEnum.Cancelled;
    case AppointmentStatus.LATE_CANCELLED:
      return CommonStatusEnum.LateCancelled;
    case AppointmentStatus.NO_SHOW:
      return verbose
        ? appointment.noShowReason === NoShowReason.PATIENT_DID_NOT_ATTEND
          ? CommonStatusEnum.NoShowPatient
          : CommonStatusEnum.NoShowInterpreter
        : CommonStatusEnum.NoShow;
    case AppointmentStatus.RESCHEDULED:
      return verbose
        ? appointment.reschedulingRequestedInternally
          ? CommonStatusEnum.RescheduledInternal
          : appointment.reschedulingRequestedBy === ParticipantType.PATIENT
            ? CommonStatusEnum.RescheduledPatient
            : appointment.reschedulingRequestedBy === ParticipantType.ADJUSTER
              ? CommonStatusEnum.RescheduledADJ
              : appointment.reschedulingRequestedBy &&
                  new Set([
                    ParticipantType.APPLICANT_ATTORNEY,
                    ParticipantType.DEFENSE_ATTORNEY,
                  ]).has(appointment.reschedulingRequestedBy)
                ? CommonStatusEnum.RescheduledAADA
                : CommonStatusEnum.Rescheduled
        : CommonStatusEnum.Rescheduled;
    case AppointmentStatus.LATE_RESCHEDULED:
      return verbose
        ? appointment.reschedulingRequestedInternally
          ? CommonStatusEnum.LateRescheduledInternal
          : appointment.reschedulingRequestedBy === ParticipantType.PATIENT
            ? CommonStatusEnum.LateRescheduledPatient
            : appointment.reschedulingRequestedBy === ParticipantType.ADJUSTER
              ? CommonStatusEnum.LateRescheduledADJ
              : appointment.reschedulingRequestedBy &&
                  new Set([
                    ParticipantType.APPLICANT_ATTORNEY,
                    ParticipantType.DEFENSE_ATTORNEY,
                  ]).has(appointment.reschedulingRequestedBy)
                ? CommonStatusEnum.LateRescheduledAADA
                : CommonStatusEnum.LateRescheduled
        : CommonStatusEnum.LateRescheduled;
    case AppointmentStatus.RESCHEDULING_IN_PROGRESS:
      return CommonStatusEnum.ReschedulingInProgress;
  }

  // Report status — only reached while SCHEDULED (or an unmapped status).
  // The doctor's view is unchanged once they approve — the MA's intermediate
  // READY_TO_SUBMIT progression is invisible to the doctor, so both map to
  // "Report approved".
  switch (appointment.reportStatus) {
    case ReportStatus.APPROVED:
    case ReportStatus.READY_TO_SUBMIT:
      return DoctorStatusEnum.ReportApproved;
    case ReportStatus.SUBMITTED:
      return CommonStatusEnum.ReportSubmitted;
    case ReportStatus.READY_FOR_REVIEW:
      return CommonStatusEnum.ReportReadyForReview;
  }

  // SCHEDULED, date-derived — reached only when no report has been published yet.
  if (appointment.status === AppointmentStatus.SCHEDULED) {
    // if appointment time is earlier than today, it would be considered as completed
    const today = getTodayDateISOStr();
    const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
    if (!extractedAppointmentDate) {
      throw new Error("Appointment date is missing");
    }
    if (extractedAppointmentDate < today) {
      // Supplemental reports carry no written physician assessment. Pre-publish
      // (before READY_FOR_REVIEW, caught above) they're being processed by the
      // team, so show "Processing supplemental" rather than "Assessment required".
      if (appointment.type === AppointmentType.SUPPLEMENTAL) {
        return DoctorStatusEnum.ProcessingSupplemental;
      }
      // Written assessment still owed by the physician → "Assessment required" (action).
      // Once submitted — or when the flow is dictated so there's nothing to submit
      // in-portal — fall through to "Appointment completed" (no action).
      if (
        appointment.assessment?.markedAsDone === true ||
        appointment.reportConfiguration?.assessment === ReportSource.DICTATE
      ) {
        return CommonStatusEnum.AppointmentCompleted;
      }
      return CommonStatusEnum.AssessmentRequired;
    }
    return CommonStatusEnum.Scheduled;
  }
};

export const getDisplayStateForMedicalAssistant = (
  appointment: AppointmentStatusInfo,
  verbose = true,
): MAStatusEnum | CommonStatusEnum | undefined => {
  // Check billing states first
  switch (appointment.billingStatus) {
    case BillingStatus.CORRECTING_PAYMENTS:
      return MAStatusEnum.CorrectingPayments;
    case BillingStatus.PAYMENT_RECEIVED:
      return CommonStatusEnum.PaymentReceived;
    case BillingStatus.PHYSICIAN_PAID:
      return MAStatusEnum.PaidPhysician;
    case BillingStatus.BILLED:
      return MAStatusEnum.Billed;
  }

  // Occurrence states other than SCHEDULED take precedence over reportStatus.
  // reportStatus only advances while an appointment is on the normal SCHEDULED
  // path, so a report published before the appointment was cancelled / no-showed
  // / rescheduled / put on hold must not mask the real lifecycle state. GET-3429.
  switch (appointment.status) {
    case AppointmentStatus.CANCELLED:
      return CommonStatusEnum.Cancelled;
    case AppointmentStatus.LATE_CANCELLED:
      return CommonStatusEnum.LateCancelled;
    case AppointmentStatus.NO_SHOW:
      return verbose
        ? appointment.noShowReason === NoShowReason.PATIENT_DID_NOT_ATTEND
          ? CommonStatusEnum.NoShowPatient
          : CommonStatusEnum.NoShowInterpreter
        : CommonStatusEnum.NoShow;
    case AppointmentStatus.RESCHEDULED:
      return verbose
        ? appointment.reschedulingRequestedInternally
          ? CommonStatusEnum.RescheduledInternal
          : appointment.reschedulingRequestedBy === ParticipantType.PATIENT
            ? CommonStatusEnum.RescheduledPatient
            : appointment.reschedulingRequestedBy === ParticipantType.ADJUSTER
              ? CommonStatusEnum.RescheduledADJ
              : appointment.reschedulingRequestedBy ===
                    ParticipantType.APPLICANT_ATTORNEY ||
                  appointment.reschedulingRequestedBy ===
                    ParticipantType.DEFENSE_ATTORNEY
                ? CommonStatusEnum.RescheduledAADA
                : CommonStatusEnum.Rescheduled
        : CommonStatusEnum.Rescheduled;
    case AppointmentStatus.LATE_RESCHEDULED:
      return verbose
        ? appointment.reschedulingRequestedInternally
          ? CommonStatusEnum.LateRescheduledInternal
          : appointment.reschedulingRequestedBy === ParticipantType.PATIENT
            ? CommonStatusEnum.LateRescheduledPatient
            : appointment.reschedulingRequestedBy === ParticipantType.ADJUSTER
              ? CommonStatusEnum.LateRescheduledADJ
              : appointment.reschedulingRequestedBy &&
                  new Set([
                    ParticipantType.APPLICANT_ATTORNEY,
                    ParticipantType.DEFENSE_ATTORNEY,
                  ]).has(appointment.reschedulingRequestedBy)
                ? CommonStatusEnum.LateRescheduledAADA
                : CommonStatusEnum.LateRescheduled
        : CommonStatusEnum.LateRescheduled;
    case AppointmentStatus.INQUIRY:
      return CommonStatusEnum.Inquiry;
    case AppointmentStatus.INQUIRY_CLOSED:
      return CommonStatusEnum.InquiryClosed;
    case AppointmentStatus.RESCHEDULING_HOLD:
      return MAStatusEnum.ReschedulingHold;
    case AppointmentStatus.LATE_RESCHEDULING_HOLD:
      return MAStatusEnum.LateReschedulingHold;
    case AppointmentStatus.RESCHEDULING_IN_PROGRESS:
      return CommonStatusEnum.ReschedulingInProgress;
    case AppointmentStatus.DEPOSITION_HOLD:
      return MAStatusEnum.DepositionHold;
    case AppointmentStatus.REEVAL_HOLD:
      return MAStatusEnum.ReevalHold;
  }

  // Report status — only reached while SCHEDULED (or an unmapped status).
  switch (appointment.reportStatus) {
    case ReportStatus.APPROVED:
      // Doctor approved → MA owes a final proofread pass before submission.
      return MAStatusEnum.ReadyForFinalReview;
    case ReportStatus.READY_TO_SUBMIT:
      // MA finished the final review → cleared to submit.
      return MAStatusEnum.ReadyToSubmit;
    case ReportStatus.SUBMITTED:
      return CommonStatusEnum.ReportSubmitted;
    case ReportStatus.READY_FOR_REVIEW:
      return CommonStatusEnum.ReportReadyForReview;
  }

  // SCHEDULED, date-derived — reached only when no report has been published yet.
  if (appointment.status === AppointmentStatus.SCHEDULED) {
    const today = getTodayDateISOStr();
    const extractedAppointmentDate = extractDateWithDbDefault(appointment.date);
    if (!extractedAppointmentDate) {
      throw new Error("Appointment date is missing");
    }
    // if appointment time is earlier than today, it would be considered as completed
    if (extractedAppointmentDate < today) {
      return CommonStatusEnum.AppointmentCompleted;
    }
    return CommonStatusEnum.Scheduled;
  }
};

type StatusColor =
  | "toDoColorVariant"
  | "doneColorVariant"
  | "errorColorVariant"
  | "finalColorVariant"
  | "processingColorVariant";

const statusColorMapMAAdmin: Record<
  StatusColor,
  (MAStatusEnum | CommonStatusEnum)[]
> = {
  errorColorVariant: [
    CommonStatusEnum.Cancelled,
    CommonStatusEnum.Rescheduled,
    CommonStatusEnum.RescheduledPatient,
    CommonStatusEnum.RescheduledADJ,
    CommonStatusEnum.RescheduledAADA,
    CommonStatusEnum.RescheduledInternal,
    CommonStatusEnum.LateRescheduled,
    CommonStatusEnum.LateRescheduledPatient,
    CommonStatusEnum.LateRescheduledADJ,
    CommonStatusEnum.LateRescheduledAADA,
    CommonStatusEnum.LateRescheduledInternal,
    CommonStatusEnum.NoShow,
    CommonStatusEnum.NoShowPatient,
    CommonStatusEnum.NoShowInterpreter,
    CommonStatusEnum.InquiryClosed,
    MAStatusEnum.CorrectingPayments,
    CommonStatusEnum.LateCancelled,
  ],
  doneColorVariant: [
    CommonStatusEnum.Scheduled,
    CommonStatusEnum.ReportReadyForReview,
    MAStatusEnum.Billed,
    MAStatusEnum.ReadyToSubmit,
  ],
  toDoColorVariant: [
    CommonStatusEnum.AppointmentCompleted,
    CommonStatusEnum.ReportSubmitted,
    MAStatusEnum.ReadyForFinalReview,
  ],
  processingColorVariant: [
    CommonStatusEnum.Inquiry,
    MAStatusEnum.ReschedulingHold,
    MAStatusEnum.LateReschedulingHold,
    CommonStatusEnum.ReschedulingInProgress,
    MAStatusEnum.DepositionHold,
    MAStatusEnum.ReevalHold,
  ],
  finalColorVariant: [
    CommonStatusEnum.PaymentReceived,
    MAStatusEnum.PaidPhysician,
  ],
};

const statusColorMapDoctor: Record<
  StatusColor,
  (DoctorStatusEnum | CommonStatusEnum)[]
> = {
  doneColorVariant: [
    CommonStatusEnum.Scheduled,
    CommonStatusEnum.AppointmentCompleted,
    DoctorStatusEnum.ReportApproved,
    DoctorStatusEnum.ProcessingSupplemental,
    CommonStatusEnum.ReportSubmitted,
  ],
  errorColorVariant: [
    CommonStatusEnum.Cancelled,
    CommonStatusEnum.Rescheduled,
    CommonStatusEnum.NoShowPatient,
    CommonStatusEnum.NoShowInterpreter,
    CommonStatusEnum.LateCancelled,
    CommonStatusEnum.LateRescheduled,
    CommonStatusEnum.NoShow,
    CommonStatusEnum.RescheduledPatient,
    CommonStatusEnum.RescheduledADJ,
    CommonStatusEnum.RescheduledAADA,
    CommonStatusEnum.RescheduledInternal,
    CommonStatusEnum.LateRescheduledPatient,
    CommonStatusEnum.LateRescheduledADJ,
    CommonStatusEnum.LateRescheduledAADA,
    CommonStatusEnum.LateRescheduledInternal,
  ],
  toDoColorVariant: [
    CommonStatusEnum.AssessmentRequired,
    CommonStatusEnum.ReportReadyForReview,
  ],
  processingColorVariant: [
    CommonStatusEnum.Inquiry,
    CommonStatusEnum.ReschedulingInProgress,
  ],
  finalColorVariant: [CommonStatusEnum.PaymentReceived],
};

const getStatusColor = (
  role: AuthUserGroup | null,
  status: AppointmentStatusEnum,
): StatusColor => {
  if (!role) return "toDoColorVariant";

  const map =
    role === AuthUserGroup.DOCTOR
      ? statusColorMapDoctor
      : [
            AuthUserGroup.MEDICAL_ASSISTANT,
            AuthUserGroup.ADMIN,
            AuthUserGroup.HISTORIAN,
          ].includes(role)
        ? statusColorMapMAAdmin
        : undefined;

  if (!map) return "toDoColorVariant";

  const foundColor = (
    Object.entries(map) as [StatusColor, AppointmentStatusEnum[]][]
  ).find(([, statuses]) => statuses.includes(status))?.[0];

  return foundColor ?? "toDoColorVariant";
};

export const getStatusChipVariant = (
  role: AuthUserGroup | null,
  status: AppointmentStatusEnum,
): StatusChipVariant => {
  const color = getStatusColor(role, status);

  if (color === "doneColorVariant") return "done";
  if (color === "errorColorVariant") return "error";
  if (color === "processingColorVariant") return "processing";
  if (color === "finalColorVariant") return "final";
  return "todo";
};
